import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, SearchX, Expand, X } from "lucide-react";
import { format, eachDayOfInterval, subDays, parseISO, differenceInCalendarDays } from "date-fns";
import { toast } from "sonner";
import { StayHeader } from "@/components/stays/StayHeader";
import { PropertyCard } from "@/components/stays/PropertyCard";
import { PropertySection } from "@/components/stays/PropertySection";
import { SearchWidget } from "@/components/stays/SearchWidget";
import { HeroFlipWord, LiveActivityPills } from "@/components/stays/HomeLiveElements";
import { CategoryTabs } from "@/components/stays/CategoryTabs";
import { PriceFilter } from "@/components/stays/PriceFilter";
import { MobileBottomNav } from "@/components/stays/MobileBottomNav";
import { MobileExploreHeader } from "@/components/stays/MobileExploreHeader";
import { SearchOverlay } from "@/components/stays/search/SearchOverlay";
import { StaysMap } from "@/components/stays/StaysMap";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { GridSkeleton } from "@/components/stays/Skeletons";
import Footer from "@/components/Footer";
import { getProperties, getRooms, getAvailability, getMapPoints, distanceKm, friendlyError } from "@/lib/stayflowApi";
import { matchesCategory } from "@/lib/stayCategories";
import { setMeta, SITE_URL } from "@/lib/seo";

const iso = (d) => format(d, "yyyy-MM-dd");

const StaysList = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["properties"],
    queryFn: getProperties,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  useEffect(() => {
    setMeta({
      title: "Stays — StayFlow",
      description: "Discover handpicked homestays, hotels and resorts. Filter by city, price and dates. Book instantly with secure payments.",
      url: `${SITE_URL}/stays`,
    });
  }, []);

  const properties = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [sp] = useSearchParams();
  const searchRef = useRef(null);

  // map / geo data (resolves the app's location links to coordinates + city)
  const mapQ = useQuery({ queryKey: ["map-points"], queryFn: getMapPoints, staleTime: 10 * 60 * 1000, retry: 2 });
  const geoList = useMemo(() => (Array.isArray(mapQ.data) ? mapQ.data : []), [mapQ.data]);
  const geoById = useMemo(() => Object.fromEntries(geoList.map((g) => [g.id, g])), [geoList]);

  // "Near Me" user location from ?near=lat,lng
  const userLoc = useMemo(() => {
    const n = sp.get("near");
    if (!n) return null;
    const [lat, lng] = n.split(",").map(Number);
    return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
  }, [sp]);

  // ---- filter state (seeded from search params) ----
  const [search, setSearch] = useState(() => sp.get("location") || "");
  const [category, setCategory] = useState("all");
  const [dateRange, setDateRange] = useState(() => {
    const ci = sp.get("checkIn"), co = sp.get("checkOut");
    try { if (ci && co) return { from: parseISO(ci), to: parseISO(co) }; }
    catch (e) { console.warn("Ignoring invalid checkIn/checkOut URL params:", e); }
    return undefined;
  });
  const guestsParamRaw = useMemo(() => ({
    rooms: Number(sp.get("rooms")) || 1,
    adults: Number(sp.get("adults")) || 2,
    children: Number(sp.get("children")) || 0,
  }), [sp]);
  const [rooms, setRooms] = useState(guestsParamRaw.rooms);
  const [adults, setAdults] = useState(guestsParamRaw.adults);
  const [children, setChildren] = useState(guestsParamRaw.children);
  const guestsCount = adults + children;
  const [locating, setLocating] = useState(false);


  const priceBounds = useMemo(() => {
    const prices = properties.map((p) => p.min_price_per_night).filter((n) => typeof n === "number");
    if (prices.length === 0) return [0, 0];
    let lo = Math.floor(Math.min(...prices) / 100) * 100;
    const hi = Math.ceil(Math.max(...prices) / 100) * 100;
    // Widen when all inventory shares one price bucket so the slider stays usable.
    if (lo >= hi) lo = Math.max(0, hi - 500);
    return [lo, hi];
  }, [properties]);

  const [priceRange, setPriceRange] = useState(priceBounds);
  useEffect(() => { setPriceRange(priceBounds); }, [priceBounds]);

  const hayOf = (p, g) => [p.name, p.city, p.state, p.address, p.type, p.property_type, g.city, g.state, g.address].filter(Boolean).join(" ");
  // Category matching uses only the property name/type (not city/address) to
  // avoid false positives like "Golaghat" matching the "River View" chip.
  const catHayOf = (p) => [p.name, p.type, p.property_type].filter(Boolean).join(" ");

  // ---- text / category / price filtering (instant, client-side) ----
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return properties.filter((p) => {
      const g = geoById[p.id] || {};
      const hay = hayOf(p, g);
      if (!matchesCategory(catHayOf(p), category)) return false;
      if (typeof p.min_price_per_night === "number") {
        if (p.min_price_per_night < priceRange[0] || p.min_price_per_night > priceRange[1]) return false;
      }
      if (q && !hay.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [properties, category, priceRange, search, geoById]);

  // ---- date availability filtering (best-effort, fail-open) ----
  const [availableIds, setAvailableIds] = useState(null);
  const [checkingDates, setCheckingDates] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const cacheRef = useRef({});
  const baseFilteredRef = useRef(baseFiltered);
  baseFilteredRef.current = baseFiltered;

  const baseKey = baseFiltered.map((p) => p.id).join(",");
  const rangeKey = dateRange?.from && dateRange?.to ? `${iso(dateRange.from)}_${iso(dateRange.to)}` : "";
  const nights = rangeKey ? differenceInCalendarDays(parseISO(rangeKey.split("_")[1]), parseISO(rangeKey.split("_")[0])) : 0;

  useEffect(() => {
    let cancelled = false;
    if (!rangeKey) { setAvailableIds(null); setCheckingDates(false); return; }
    const [start, end] = rangeKey.split("_");
    const busyNights = eachDayOfInterval({ start: parseISO(start), end: subDays(parseISO(end), 1) }).map(iso);
    const roomFree = (busy) => { const set = new Set(busy || []); return !busyNights.some((n) => set.has(n)); };

    setCheckingDates(true);
    (async () => {
      const results = await Promise.all(baseFilteredRef.current.map(async (p) => {
        const ck = `${p.id}|${start}|${end}`;
        if (cacheRef.current[ck] !== undefined) return { id: p.id, ok: cacheRef.current[ck] };
        try {
          const rs = await getRooms(p.id);
          const checks = await Promise.all((rs || []).map(async (r) => {
            try { const a = await getAvailability(r.id, start, end); return roomFree(a?.busy_days); }
            catch { return true; }
          }));
          const ok = checks.length === 0 ? true : checks.some(Boolean);
          cacheRef.current[ck] = ok;
          return { id: p.id, ok };
        } catch {
          return { id: p.id, ok: true };
        }
      }));
      if (cancelled) return;
      setAvailableIds(new Set(results.filter((r) => r.ok).map((r) => r.id)));
      setCheckingDates(false);
    })();
    return () => { cancelled = true; };
  }, [rangeKey, baseKey]);

  const finalList = useMemo(() => {
    let list = (!rangeKey || availableIds === null) ? baseFiltered : baseFiltered.filter((p) => availableIds.has(p.id));
    if (userLoc) {
      list = [...list].sort((a, b) => distanceKm(userLoc, geoById[a.id]) - distanceKm(userLoc, geoById[b.id]));
    }
    return list;
  }, [baseFiltered, availableIds, rangeKey, userLoc, geoById]);

  const priceChanged = priceRange[0] > priceBounds[0] || priceRange[1] < priceBounds[1];
  const activeCount =
    (search.trim() ? 1 : 0) +
    (category !== "all" ? 1 : 0) +
    (priceChanged ? 1 : 0) +
    (rangeKey ? 1 : 0);

  const clearFilters = () => { setSearch(""); setCategory("all"); setPriceRange(priceBounds); setDateRange(undefined); };

  const dateQuery = (() => {
    const p = new URLSearchParams();
    if (rangeKey) { p.set("checkIn", rangeKey.split("_")[0]); p.set("checkOut", rangeKey.split("_")[1]); }
    if (guestsCount) p.set("guests", String(guestsCount));
    p.set("rooms", String(rooms));
    p.set("adults", String(adults));
    p.set("children", String(children));
    const s = p.toString();
    return s ? `?${s}` : "";
  })();

  // discovery = no active filters and no geo sort → show curated rows grouped by city
  const isDiscovery = activeCount === 0 && !userLoc;

  const cityGroups = useMemo(() => {
    if (!isDiscovery) return [];
    const m = new Map();
    finalList.forEach((p) => {
      const g = geoById[p.id] || {};
      const c = p.city || g.city || "More stays";
      if (!m.has(c)) m.set(c, []);
      m.get(c).push(p);
    });
    return [...m.entries()];
  }, [finalList, geoById, isDiscovery]);

  // Tapping the hero Search button transitions to the split-screen map results page.
  const goToMapSearch = () => {
    const p = new URLSearchParams();
    if (search.trim()) p.set("location", search.trim());
    if (rangeKey) { const [ci, co] = rangeKey.split("_"); p.set("checkIn", ci); p.set("checkOut", co); }
    p.set("adults", String(adults));
    p.set("children", String(children));
    p.set("rooms", String(rooms));
    navigate(`/stays/search?${p.toString()}`);
  };

  const searchParamsBase = () => {
    const p = new URLSearchParams();
    if (rangeKey) { const [ci, co] = rangeKey.split("_"); p.set("checkIn", ci); p.set("checkOut", co); }
    p.set("rooms", String(rooms)); p.set("adults", String(adults)); p.set("children", String(children));
    return p;
  };

  const pickPlace = (place) => {
    const p = searchParamsBase();
    p.set("place", place.main); p.set("placeId", place.place_id);
    navigate(`/stays/search?${p.toString()}`);
  };

  const openStay = (stay) => navigate(`/stays/${stay.id}${dateQuery}`);

  const nearMe = () => {
    if (!("geolocation" in navigator)) { toast.error("Location isn't supported on this device."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const { latitude, longitude } = pos.coords;
        const p = new URLSearchParams();
        if (rangeKey) { p.set("checkIn", rangeKey.split("_")[0]); p.set("checkOut", rangeKey.split("_")[1]); }
        p.set("rooms", String(rooms)); p.set("adults", String(adults)); p.set("children", String(children));
        p.set("near", `${latitude.toFixed(5)},${longitude.toFixed(5)}`);
        navigate(`/stays/search?${p.toString()}`);
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? "Please allow location access to use Near Me." : "Couldn't get your location. Try again.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const hasProps = !isLoading && !isError && properties.length > 0;

  // ---- mobile app-shell (search sheet + capsule labels) ----
  const [searchSheetOpen, setSearchSheetOpen] = useState(false);
  const filtersOn = activeCount > 0 || !!userLoc;
  const capsuleTitle = search.trim() || (filtersOn ? "Anywhere" : "Start your search");
  const capsuleSub = filtersOn
    ? [
        rangeKey ? `${format(dateRange.from, "dd MMM")} – ${format(dateRange.to, "dd MMM")}` : "Any week",
        `${guestsCount} guest${guestsCount !== 1 ? "s" : ""}`,
        userLoc ? "Near me" : null,
      ].filter(Boolean).join(" · ")
    : "Anywhere · Any week · Add guests";

  return (
    <div className="bg-paper min-h-screen pb-24 md:pb-0" data-testid="stays-page">
      <StayHeader className="hidden md:block" />

      {/* ---- Mobile app shell: floating search capsule + category pills ---- */}
      <MobileExploreHeader
        onOpenSearch={() => setSearchSheetOpen(true)}
        title={capsuleTitle}
        subtitle={capsuleSub}
        activeCount={activeCount}
        category={category}
        setCategory={setCategory}
      />

      {/* ---- Hero + segmented search (desktop) ---- */}
      <div ref={searchRef} className="hidden md:block scroll-mt-24 border-b border-black/5 bg-gradient-to-b from-brand-soft/50 to-paper">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-8 md:pb-10">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-3xl mx-auto">
            <p className="overline text-brand">Book a stay</p>
            <h1 className="mt-3 font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]">
              <span className="block">Find your next</span>
              <HeroFlipWord />
            </h1>
            <p className="mt-4 text-ink-soft text-base md:text-lg">
              Handpicked homestays, hotels & resorts — real-time availability, instant confirmation.
            </p>
          </motion.div>

          {hasProps && (
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mt-8">
              <SearchWidget
                search={search} setSearch={setSearch} stays={properties}
                onPickPlace={pickPlace} onPickStay={openStay}
                dateRange={dateRange} setDateRange={setDateRange}
                rooms={rooms} setRooms={setRooms}
                adults={adults} setAdults={setAdults}
                childrenCount={children} setChildren={setChildren}
                onSearch={goToMapSearch} onNearMe={nearMe} locating={locating}
              />
            </motion.div>
          )}
          {hasProps && <LiveActivityPills />}
        </div>
      </div>

      {/* ---- Sticky category + price bar (desktop) ---- */}
      {hasProps && (
        <div className="hidden md:block sticky top-[70px] z-30 bg-paper/90 backdrop-blur-xl border-b border-black/5" data-testid="stays-toolbar">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-2.5 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <CategoryTabs category={category} setCategory={setCategory} />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="hidden md:inline-flex"><PriceFilter priceRange={priceRange} setPriceRange={setPriceRange} priceBounds={priceBounds} /></span>
              {activeCount > 0 && (
                <button onClick={clearFilters} data-testid="filter-clear" className="h-10 inline-flex items-center gap-1.5 rounded-full px-3.5 md:px-4 text-sm font-medium text-brand hover:bg-brand-soft transition-colors shrink-0">
                  <X className="w-4 h-4" /> <span className="hidden sm:inline">Clear</span> ({activeCount})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-10">
        {/* results header row */}
        {hasProps && (
          <div className="flex items-center justify-between gap-4 mb-6">
            <p className="text-sm text-ink-soft" data-testid="filter-result-count">
              {checkingDates ? "Checking availability…" : `${finalList.length} stay${finalList.length !== 1 ? "s" : ""} available`}
              {userLoc && <span className="ml-2 text-brand font-medium" data-testid="near-me-banner">· 📍 nearest to you</span>}
            </p>
            <button onClick={() => setMapOpen(true)} data-testid="map-open-btn" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3.5 py-2 text-sm font-medium text-ink hover:border-brand hover:text-brand transition-colors">
              <Expand className="w-4 h-4" /> Map
            </button>
          </div>
        )}

        {isLoading && <GridSkeleton count={6} />}

        {isError && (
          <div className="rounded-3xl border border-black/5 bg-white p-10 text-center" data-testid="stays-error">
            <p className="text-ink font-medium">{friendlyError(error)}</p>
            <button onClick={() => refetch()} data-testid="stays-retry" className="mt-4 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
              Try again
            </button>
          </div>
        )}

        {!isLoading && !isError && properties.length === 0 && (
          <div className="rounded-3xl border border-black/5 bg-white p-16 text-center" data-testid="stays-empty">
            <HomeIcon className="w-10 h-10 mx-auto text-ink-faint" aria-hidden="true" />
            <p className="mt-4 font-heading font-bold text-xl text-ink">New stays coming soon.</p>
            <p className="mt-2 text-ink-soft">We're getting our properties ready. Please check back shortly.</p>
          </div>
        )}

        {hasProps && finalList.length === 0 && (
          <div className="rounded-3xl border border-black/5 bg-white p-16 text-center" data-testid="stays-no-results">
            <SearchX className="w-10 h-10 mx-auto text-ink-faint" aria-hidden="true" />
            <p className="mt-4 font-heading font-bold text-xl text-ink">No stays match your filters.</p>
            <p className="mt-2 text-ink-soft">Try widening your dates, price or category.</p>
            <button onClick={clearFilters} className="mt-4 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-medium">Clear filters</button>
          </div>
        )}

        {/* discovery: curated horizontal rows grouped by city */}
        {hasProps && finalList.length > 0 && isDiscovery && (
          <div className={`transition-opacity ${checkingDates ? "opacity-60" : ""}`} data-testid="discovery-sections">
            {cityGroups.map(([cityName, list]) => (
              <PropertySection
                key={cityName}
                title={cityName === "More stays" ? "More stays" : `Popular stays in ${cityName}`}
                properties={list}
                query={dateQuery}
                geoById={geoById}
                nights={nights}
                testid={`section-${cityName}`}
              />
            ))}
          </div>
        )}

        {/* filtered: responsive grid */}
        {hasProps && finalList.length > 0 && !isDiscovery && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-8 transition-opacity ${checkingDates ? "opacity-60" : ""}`} data-testid="properties-grid">
            {finalList.map((p, i) => <PropertyCard key={p.id} property={p} index={i} query={dateQuery} locationOverride={geoById[p.id]} nights={nights} />)}
          </div>
        )}
      </main>

      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 gap-0 overflow-hidden rounded-2xl" data-testid="map-dialog">
          <div className="px-5 py-3.5 border-b border-black/5">
            <DialogTitle className="font-heading font-bold text-lg text-ink">Explore stays on the map</DialogTitle>
          </div>
          <div className="p-3">
            {mapOpen && <StaysMap points={geoList} userLoc={userLoc} height={540} testid="stays-map-large" />}
          </div>
        </DialogContent>
      </Dialog>

      <SearchOverlay
        open={searchSheetOpen}
        onClose={() => setSearchSheetOpen(false)}
        initial={{
          search, dateRange, adults, children, rooms,
          near: userLoc,
          flex: Number(sp.get("flex")) || 0,
          infants: Number(sp.get("infants")) || 0,
          pets: Number(sp.get("pets")) || 0,
        }}
        stays={properties} onPickStay={openStay}
        onSearch={(params) => navigate(`/stays/search?${params.toString()}`)}
      />

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default StaysList;
